"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Eye, Download, Loader2, TrendingUp, Building2, Briefcase } from "lucide-react";
import { cn } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import { AudiencePreviewData } from "../../types/audienceTypes";

// ============================================================================
// Animated Counter Component
// ============================================================================

interface AnimatedCounterProps {
  value: number;
  className?: string;
}

function AnimatedCounter({ value, className }: AnimatedCounterProps) {
  const [displayValue, setDisplayValue] = useState(0);
  const prevValue = useRef(0);

  useEffect(() => {
    const startValue = prevValue.current;
    const endValue = value;
    const duration = 800;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out-expo)
      const eased = 1 - Math.pow(1 - progress, 4);

      const currentValue = Math.round(startValue + (endValue - startValue) * eased);
      setDisplayValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        prevValue.current = endValue;
      }
    };

    requestAnimationFrame(animate);
  }, [value]);

  return (
    <span className={className}>
      {displayValue.toLocaleString()}
    </span>
  );
}

// ============================================================================
// Progress Bar Component
// ============================================================================

interface ProgressBarProps {
  percentage: number;
  color?: string;
  animate?: boolean;
}

function ProgressBar({ percentage, color = "var(--ab-accent)", animate = true }: ProgressBarProps) {
  return (
    <div className="h-2 bg-[var(--ab-bg-primary)] rounded-full overflow-hidden">
      <motion.div
        animate={{ width: `${percentage}%` }}
        className="h-full rounded-full"
        initial={{ width: 0 }}
        style={{ backgroundColor: color }}
        transition={{ duration: animate ? 0.8 : 0, ease: "easeOut" }}
      />
    </div>
  );
}

// ============================================================================
// Component Props
// ============================================================================

interface AudiencePreviewPanelProps {
  preview: AudiencePreviewData;
  filterCount: number;
}

// ============================================================================
// Main Component
// ============================================================================

export function AudiencePreviewPanel({ preview, filterCount }: AudiencePreviewPanelProps) {
  const [showContacts, setShowContacts] = useState(false);

  const isEmpty = filterCount === 0;
  const isProcessing = preview.isProcessing;

  // Color palette for breakdown charts
  const colors = [
    "#f97316", // Orange
    "#3b82f6", // Blue
    "#22c55e", // Green
    "#a855f7", // Purple
    "#ec4899", // Pink
    "#06b6d4", // Cyan
  ];

  return (
    <div className="ab-card overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[var(--ab-border)] bg-gradient-to-r from-[var(--ab-bg-tertiary)]/50 to-transparent">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[var(--ab-accent)]/20 flex items-center justify-center">
            <Users className="w-4 h-4 text-[var(--ab-accent)]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[var(--ab-text-primary)]">
              Audience Preview
            </h3>
            <p className="text-xs text-[var(--ab-text-muted)]">
              {filterCount} {filterCount === 1 ? "filter" : "filters"} applied
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-6">
        {/* Count Display */}
        <div className="text-center py-6">
          {/* @ts-ignore - framer-motion type incompatibility with React 18 */}
          <AnimatePresence mode="wait">
            {isEmpty ? (
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                initial={{ opacity: 0, scale: 0.9 }}
                key="empty"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--ab-bg-tertiary)] flex items-center justify-center">
                  <Users className="w-8 h-8 text-[var(--ab-text-muted)]" />
                </div>
                <p className="text-[var(--ab-text-muted)] text-sm">
                  Add filters to see your audience
                </p>
              </motion.div>
            ) : isProcessing ? (
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                initial={{ opacity: 0, scale: 0.9 }}
                key="processing"
              >
                <Loader2 className="w-12 h-12 mx-auto mb-4 text-[var(--ab-accent)] animate-spin" />
                <p className="text-[var(--ab-text-secondary)] text-sm">
                  Calculating audience...
                </p>
              </motion.div>
            ) : (
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                initial={{ opacity: 0, scale: 0.9 }}
                key="count"
              >
                <div className="relative inline-block">
                  {/* Glow effect */}
                  <div className="absolute inset-0 blur-2xl bg-[var(--ab-accent)]/20 rounded-full" />

                  {/* Count */}
                  <div className="relative">
                    <AnimatedCounter
                      className="text-5xl font-bold ab-count-display ab-mono"
                      value={preview.totalCount}
                    />
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      className="absolute -top-1 -right-1"
                      transition={{ duration: 0.5, delay: 0.3 }}
                    >
                      <TrendingUp className="w-5 h-5 text-[var(--ab-success)]" />
                    </motion.div>
                  </div>
                </div>

                <p className="text-[var(--ab-text-secondary)] text-sm mt-2">
                  contacts match your criteria
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Breakdown Charts */}
        {!isEmpty && !isProcessing && preview.breakdown.length > 0 && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="space-y-5"
            initial={{ opacity: 0, y: 10 }}
            transition={{ delay: 0.3 }}
          >
            {preview.breakdown.map((breakdown, breakdownIndex) => (
              <div key={breakdown.field}>
                <div className="flex items-center gap-2 mb-3">
                  {breakdown.field === "trade" ? (
                    <Building2 className="w-4 h-4 text-[var(--ab-text-muted)]" />
                  ) : (
                    <Briefcase className="w-4 h-4 text-[var(--ab-text-muted)]" />
                  )}
                  <span className="text-xs font-medium text-[var(--ab-text-secondary)] uppercase tracking-wider">
                    {breakdown.label}
                  </span>
                </div>

                <div className="space-y-2.5">
                  {breakdown.segments.map((segment, segmentIndex) => (
                    <motion.div
                      animate={{ opacity: 1, x: 0 }}
                      initial={{ opacity: 0, x: -10 }}
                      key={segment.label}
                      transition={{ delay: 0.1 * segmentIndex + 0.4 }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-[var(--ab-text-primary)]">
                          {segment.label}
                        </span>
                        <span className="text-xs text-[var(--ab-text-muted)] ab-mono">
                          {segment.count.toLocaleString()} ({segment.percentage}%)
                        </span>
                      </div>
                      <ProgressBar
                        color={colors[segmentIndex % colors.length]}
                        percentage={segment.percentage}
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* Sample Contacts */}
        {!isEmpty && !isProcessing && preview.sampleContacts.length > 0 && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 10 }}
            transition={{ delay: 0.5 }}
          >
            <button
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-[var(--ab-bg-primary)] border border-[var(--ab-border)] hover:border-[var(--ab-border-hover)] transition-all"
              onClick={() => setShowContacts(!showContacts)}
            >
              <span className="text-sm text-[var(--ab-text-secondary)]">
                Sample Contacts
              </span>
              <motion.div
                animate={{ rotate: showContacts ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <Eye className="w-4 h-4 text-[var(--ab-text-muted)]" />
              </motion.div>
            </button>

            {/* @ts-ignore - framer-motion type incompatibility with React 18 */}
            <AnimatePresence>
              {showContacts && (
                <motion.div
                  animate={{ height: "auto", opacity: 1 }}
                  className="overflow-hidden"
                  exit={{ height: 0, opacity: 0 }}
                  initial={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="pt-3 space-y-2">
                    {preview.sampleContacts.map((contact, index) => (
                      <motion.div
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-3 p-3 rounded-lg bg-[var(--ab-bg-primary)] border border-[var(--ab-border)]"
                        initial={{ opacity: 0, x: -10 }}
                        key={contact.id}
                        transition={{ delay: index * 0.05 }}
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--ab-accent)] to-orange-600 flex items-center justify-center text-white text-xs font-semibold">
                          {contact.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[var(--ab-text-primary)] truncate">
                            {contact.name}
                          </p>
                          <p className="text-xs text-[var(--ab-text-muted)] truncate">
                            {contact.email}
                          </p>
                        </div>
                        {contact.company && (
                          <span className="text-xs text-[var(--ab-text-muted)] bg-[var(--ab-bg-tertiary)] px-2 py-1 rounded">
                            {contact.company}
                          </span>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Actions */}
        {!isEmpty && !isProcessing && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-2"
            initial={{ opacity: 0, y: 10 }}
            transition={{ delay: 0.6 }}
          >
            <Button
              className="flex-1 text-[var(--ab-text-secondary)] hover:text-[var(--ab-text-primary)] border-[var(--ab-border)] bg-transparent"
              variant="outline"
            >
              <Eye className="w-4 h-4 mr-2" />
              View All
            </Button>
            <Button
              className="flex-1 text-[var(--ab-text-secondary)] hover:text-[var(--ab-text-primary)] border-[var(--ab-border)] bg-transparent"
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default AudiencePreviewPanel;
